import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, TextField } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import type { IManufacturer, IResponseWithLoading } from "App/Modules/Models";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import type { IManufacturersRepository } from "App/Modules/Repositories";

type AddManufacturerProps = {
    manufacturersRepository: IManufacturersRepository;
    onAddSuccess?: (manufacturer: IManufacturer) => void;
    onClose: () => void;
};

export const AddManufacturer = (props: AddManufacturerProps) => {
    const { t } = useTranslation();

    const [name, setName] = useState("");
    const [corporateIdNumber, setCorporateIdNumber] = useState("");
    const [vatId, setVatId] = useState("");
    const [nameValid, setNameValid] = useState(true);
    const [saveError, setSaveError] = useState(false);

    const validateName = () => {
        let valid = true;
        if (name.length === 0) {
            valid = false;
        }
        setNameValid(valid);
        return valid;
    };

    const validateForm = () => validateName();

    const addManufacturer = () => {
        const manufacturer: IManufacturer = {
            name,
            corporateIdNumber,
            vatId,
        } as IManufacturer;

        extendWithLoadingAndFail(props.manufacturersRepository.addManufacturer(manufacturer)).subscribe(
            (response: IResponseWithLoading<IManufacturer>) => {
                if (!response.isLoading && response.data) {
                    if (props.onAddSuccess) {
                        props.onAddSuccess(response.data);
                    }
                    props.onClose();
                }
            },
            () => {
                setSaveError(true);
            },
        );
    };

    return (
        <div className="card dialog">
            <form className="dialog__content form">
                <div className="dialog__section dialog__section--full">
                    <h1 className="title title--h1 title--no-spacing">
                        {t("Gui.Buttons.AddEntity", {
                            entityName: t("Entities.Manufacturer.Manufacturer"),
                        })}
                    </h1>
                </div>

                <div className="dialog__section dialog__section--full">
                    <label className="caption" htmlFor="name">
                        {t("Entities.Common.Name")}
                    </label>
                    <TextField
                        required
                        fullWidth
                        autoComplete="off"
                        id="name"
                        variant="outlined"
                        size="small"
                        type="text"
                        helperText={!nameValid ? t("Validation.Mixed.Required") : null}
                        error={!nameValid}
                        value={name}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                        onBlur={() => validateName()}
                    />
                </div>

                <div className="dialog__section dialog__section--full">
                    <label className="caption" htmlFor="corporate-id-number">
                        {t("Entities.Common.CorporateIdNumber")}
                    </label>
                    <TextField
                        required
                        fullWidth
                        autoComplete="off"
                        id="corporate-id-number"
                        variant="outlined"
                        size="small"
                        type="text"
                        value={corporateIdNumber}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setCorporateIdNumber(event.target.value)
                        }
                    />
                </div>

                <div className="dialog__section dialog__section--full">
                    <label className="caption" htmlFor="vat-id">
                        {t("Entities.Manufacturer.VatId")}
                    </label>
                    <TextField
                        required
                        fullWidth
                        autoComplete="off"
                        id="vat-id"
                        variant="outlined"
                        size="small"
                        type="text"
                        value={vatId}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setVatId(event.target.value)}
                    />
                </div>

                {saveError ? (
                    <div className="dialog__section dialog__section--full dialog__section--noscroll">
                        <div className="notice notice--critical">
                            <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                            <p className="caption">{t("Error.ErrorSaving")}</p>
                        </div>
                    </div>
                ) : null}

                <div className="dialog__section dialog__section--buttons">
                    <Button size="small" color="default" onClick={() => props.onClose()}>
                        {t("Gui.Buttons.Cancel")}
                    </Button>

                    <Button
                        type="button"
                        size="small"
                        color="primary"
                        onClick={() => {
                            if (validateForm()) {
                                addManufacturer();
                            }
                        }}
                    >
                        {t("Gui.Buttons.Add")}
                    </Button>
                </div>
            </form>
        </div>
    );
};
